import type { AlmTool } from "@/backend/RpcBackend";

interface AlmConfig {
  displayName: string;
  icon: string;
  learnMoreSlug: string;
  steps: AlmStep[];
  manualUserMapping: boolean;
  autoUserMapping: boolean;
  mappingErrorCheck: boolean;
  fieldMapping: boolean;
  devLinks: boolean;
}

interface AlmStep {
  id: string;
  label: string;
}

const CONNECTION = { id: "Connection", label: "Connection" };
const PROJECT = { id: "Project", label: "Project" };
const WORKSPACE = { id: "Workspace", label: "Workspace" };
const WEBHOOK = { id: "Webhook", label: "Webhook" };
const TEAM_MAPPING = { id: "TeamMapping", label: "Team Mapping" };
const ART_MAPPING = { id: "ArtMapping", label: "ART Mapping" };
const USER_MAPPING = { id: "UserMapping", label: "User Mapping" };

const ALM_CONFIG: Record<AlmTool, AlmConfig> = processSteps({
  jira: {
    displayName: "Jira",
    icon: "alm/jira-color",
    learnMoreSlug: "jira",
    steps: [CONNECTION, WEBHOOK, TEAM_MAPPING, ART_MAPPING, USER_MAPPING],
    manualUserMapping: true,
    autoUserMapping: true,
    mappingErrorCheck: true,
    fieldMapping: true,
    devLinks: true,
  },
  ac: {
    displayName: "Rally",
    icon: "alm/ac-color",
    learnMoreSlug: "rally",
    steps: [CONNECTION, WORKSPACE, WEBHOOK, TEAM_MAPPING, ART_MAPPING, USER_MAPPING],
    manualUserMapping: true,
    autoUserMapping: false,
    mappingErrorCheck: false,
    fieldMapping: false,
    devLinks: false,
  },
  ado: {
    displayName: "Azure DevOps",
    icon: "alm/ado-color",
    learnMoreSlug: "ado",
    steps: [CONNECTION, PROJECT, WEBHOOK, TEAM_MAPPING, ART_MAPPING, USER_MAPPING],
    manualUserMapping: false,
    autoUserMapping: false,
    mappingErrorCheck: false,
    fieldMapping: false,
    devLinks: false,
  },
});

const FALLBACK_ALM: AlmConfig = {
  displayName: "ALM",
  icon: "general/alm-connection",
  learnMoreSlug: "",
  steps: [],
  manualUserMapping: false,
  autoUserMapping: false,
  mappingErrorCheck: false,
  fieldMapping: false,
  devLinks: false,
};

function processSteps(configs: Record<AlmTool, AlmConfig>): Record<AlmTool, AlmConfig> {
  for (const config of Object.values(configs)) {
    if (!config.manualUserMapping && !config.autoUserMapping) {
      config.steps = config.steps.filter((step) => step !== USER_MAPPING);
    }
  }
  return configs;
}

export function almConfig(almType: string | undefined): AlmConfig {
  return Object.hasOwn(ALM_CONFIG, almType!) ? ALM_CONFIG[almType as AlmTool] : FALLBACK_ALM;
}

export function learnMoreIds(almType: string | undefined, ...ids: string[]): string[] {
  const { learnMoreSlug } = almConfig(almType);
  return learnMoreSlug ? ids.map((id) => id + "-" + learnMoreSlug) : [];
}

export const ALM_SYNC_STATUS: Record<string, { label: string; dot: string }> = {
  running: { label: "Synced", dot: "success" },
  starting: { label: "Syncing", dot: "inactive" },
  stopped: { label: "Sync Stopped", dot: "inactive" },
  error: { label: "Sync Error", dot: "error" },
};
